import { defineStore } from "pinia";
import { ref, computed } from "vue";

export type ModalName =
  | "SpecialConditionModal"
  | "FileModal"
  | "LayerModal"
  | "StagesTableModal"
  | "CompareModal"
  | "MaterialMappingModal"
  | "ModalConfirm"
  | "AssignLifeTimeModal"
  | "LifetimeTable"
  | "CreateMaterialModal"
  | "IFCUploadModal"
  | "ProcessingModal"
  | "ModalMarkdown"
  | "InspectMaterial"
  | "MaterialMappingExcelUploadModal"
  | "MaterialServiceLifetimeSelectorModal"
  | "WasteSortingExcelUploadModal"
  | "DiscardModal"
  | "WasteMaterialDropModal"
  | "WasteMultipleMaterialDropModal"
  | "WasteSortingEditModal"
  | "WasteMaterialDetailModal"
  | "WasteSyncAmountsModal"
  | "WasteSyncPickupEditModal"
  | "WasteMaterialsInfoModal"
  | "DeleteModal"
  | "CustomDataSourcesModal"
  | "BillingDetailsModal"
  | "HotspotDeepDiveModal"
  | "ProjectStatusModal"
  | "SpeckleModal"
  | "RestoreProjectVersionModal"
  | "CloneProjectStateModal"
  | "SaveProjectRevisionModal"
  | "SaveRevisionFromVersionModal"
  | "MakeRevisionCurrentModal"
  | "RenameProjectRevisionModal"
  | "DeleteProjectRevisionModal"
  | "ReportDisclaimerModal"
  | "StartProjectModal"
  | "AutoMappingLeaveModal";

type AnyProps = Record<string, any>;
export interface CurrentModal {
  id: string;
  name: ModalName;
  props?: AnyProps;
  resolve?: (v: any) => void;
  reject?: (r?: any) => void;
  stack?: boolean;
  wrapperClass?: string;
  ariaLabel?: string;
  /** Doing async work: ModalRoot refuses Esc, backdrop and the close button until cleared. */
  busy?: boolean;
}

type OpenOptions = {
  wrapperClass?: string;

  stackable?: boolean;

  // What screen readers announce for the dialog. Without it ModalRoot falls back to the component
  // name, so a dialog announces as e.g. "ReportDisclaimerModal". Pass the title the user sees.
  ariaLabel?: string;
};

// Shell options a modal carries wherever it is opened from, so its callers do not each size the same
// panel. A caller's own options still win. DeleteModal is a short confirmation: ModalRoot's default
// shell (max-w-6xl, min-h 460px) would float two sentences and two buttons in a page-sized panel.
const defaultOpenOptions: Partial<Record<ModalName, OpenOptions>> = {
  DeleteModal: { wrapperClass: "max-w-md min-h-0", ariaLabel: "Confirm deletion" },
  // max-w-lg, not max-w-md like the two-button confirms: three actions in one row need the extra
  // room or "Stay" wraps onto its own line above the other two (RTLCA-5821).
  AutoMappingLeaveModal: { wrapperClass: "max-w-lg min-h-0", ariaLabel: "Leave with proposed mappings?" },
};

export const useModalStore = defineStore("modal", () => {
  const stack = ref<CurrentModal[]>([]);

  const open = (name: ModalName, props?: AnyProps, options: OpenOptions = {}) =>
    new Promise<any>((resolve, reject) => {
      const { wrapperClass = "", stackable = false, ariaLabel } = { ...defaultOpenOptions[name], ...options };

      const modal: CurrentModal = {
        id: String(Date.now()),
        name,
        props,
        resolve,
        reject,
        stack: stackable,
        wrapperClass,
        ariaLabel,
      };
      if (stackable) stack.value.push(modal);
      else stack.value = [modal];
    });

  const closeTop = (result?: any) => {
    const m = stack.value.pop();
    m?.resolve?.(result);
  };

  const close = (result?: any): number => {
    const toClose = stack.value.splice(0, stack.value.length);
    toClose.forEach((m) => m?.resolve?.(result));
    return toClose.length;
  };

  const closeByName = (name: ModalName, result?: any): boolean => {
    const idx = stack.value.findIndex((m) => m.name === name);
    if (idx === -1) return false;
    const [m] = stack.value.splice(idx, 1);
    m?.resolve?.(result);
    return true;
  };

  const dismissTop = (reason?: any) => {
    const m = stack.value.pop();
    m?.reject?.(reason);
  };

  // A modal that awaits its own work (DeleteModal with onConfirm) marks itself busy: the dialog is what
  // shows the wait, and dismissing it would only hide work that keeps running.
  const setBusy = (name: ModalName, busy: boolean) => {
    stack.value.filter((m) => m.name === name).forEach((m) => (m.busy = busy));
  };

  const current = computed(() => stack.value.at(-1) ?? null);

  return { stack, current, open, closeTop, close, closeByName, dismissTop, setBusy };
});
